import React, { Fragment, useCallback, useEffect } from 'react';
import i18n from '../../../i18n';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Hidden, makeStyles } from '@material-ui/core';

import { useAction } from 'core/store/store';

import { isUIEnabledSelector } from 'state/app/selectors';

import { RoutePath } from 'RoutePath';

import { Icon } from 'components/Icon/Icon';
import { LargeButton, LargeButtonColor } from 'components/LargeButton/LargeButton';
import { Text, TextTypography } from 'components/Text/Text';


import { ReactComponent as AddIcon } from 'assets/icons/main-16/add--16.svg';
import { ReactComponent as LinkIcon } from 'assets/icons/main-16/external-link-o--16.svg';
import { getClientUrl } from 'core/utils/utils';

export interface CatalogFooterProps {
    isOnboarding: boolean;
    onFinish: () => void;
}

const useStyles = makeStyles(theme => ({
    footer: {
        alignItems: 'center',
        borderTop: `1px solid ${theme.palette.brandPrimary.lightest}`,
        display: 'flex',
        height: theme.spacing(9),
        padding: theme.spacing(0, 3),
        position: 'fixed',
        bottom: 0,
        backgroundColor: '#fafafa',
        maxWidth: 740,
        width: '100%',
        [theme.breakpoints.down(640)]: {
            backgroundColor: '#fff',
        },
    },
    footerContent: {
        flex: 1,
    },
    footerAddButton: {
        alignItems: 'center',
        flexBasis: 'auto',
        marginRight: theme.spacing(3),
        [theme.breakpoints.down(640)]: {
            display: 'block',
            padding: theme.spacing(2),
            textAlign: 'center',
            width: '100vw',
            bottom: 70,
            position: 'fixed',
        },
    },
    footerNextButton: {
        alignItems: 'center',
        flexBasis: 'auto',
    },
    link: {
        cursor: 'pointer',
        display: 'inline-block',
    },
    linkIcon: {
        marginRight: theme.spacing(1),
        verticalAlign: 'middle',
    },
    linkText: {
        verticalAlign: 'middle',
    },
}));

export const CatalogFooter = ({ isOnboarding, onFinish }: CatalogFooterProps) => {
    const { t } = i18n.useTranslation();
    const isUIEnabled = useSelector(isUIEnabledSelector);
    useEffect(() => {
        if (isOnboarding) {
            onFinish();
        }
    }, [isOnboarding, onFinish]);


    const action = useAction();

    const handleRedirectToFeed = useCallback(() => {
        window.open(getClientUrl(), '_blank');
    }, [action, history]);

    const classes = useStyles();
    return (
        <Fragment>
            <Hidden smUp>
                <Link className={classes.footerAddButton} to={RoutePath.REGISTRATION_CATALOG_ADD}>
                    <Icon className={classes.linkIcon} component={AddIcon} size={16} svgSize={16} color="#243143" />
                    <Text typography={TextTypography.PARA_ACCENT}>{t('onboarding:emptyCatalog.addButton')}</Text>
                </Link>
            </Hidden>
            <footer className={classes.footer}>
                <div className={classes.footerContent}>
                    <div className={classes.link} onClick={handleRedirectToFeed}>
                        <Icon className={classes.linkIcon} component={LinkIcon} size={16} svgSize={16} />
                        <Text className={classes.linkText} typography={TextTypography.PARA_ACCENT}>
                            {t('onboarding:footer.aboutLink')}
                        </Text>
                    </div>
                </div>
                <Hidden xsDown>
                    <div className={classes.footerAddButton}>
                        <Link to={RoutePath.REGISTRATION_CATALOG_ADD}>
                            <Icon
                                className={classes.linkIcon}
                                component={AddIcon}
                                size={16}
                                svgSize={16}
                                color="#243143"
                            />
                            <Text typography={TextTypography.PARA_ACCENT}>
                                {t('onboarding:emptyCatalog.addButton')}
                            </Text>
                        </Link>
                    </div>
                </Hidden>
                <div className={classes.footerNextButton}>
                    {isOnboarding && (
                        <LargeButton color={LargeButtonColor.SUCCESS} disabled={!isUIEnabled} onClick={onFinish}>
                            {t('onboarding:footer.finish')}
                        </LargeButton>
                    )}
                </div>
            </footer>
        </Fragment>
    );
};
